// @ts-nocheck
import type { Saga } from 'redux-saga';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import api from 'lib/Api';
import {
  alertTypesConstants,
  userArticlesTypes,
  userProfileConstants,
  myArticlesConstants,
} from 'commons/constants';
import type { CreateArticlePayload, UpdateArticlePayload, GetUserArticles } from 'commons/types';
import alertActions from 'ducks/alert/actions';
import { getUser, getOtherUser } from 'ducks/user/selector';
import { usersArticlesActions } from './actions';
import {
  createArticleTypes,
  articlesTypes,
  updateArticleTypes,
  otherUserArticlesTypes,
  autoSaveArticleTypes,
} from './types';

const { DANGER, SUCCESS } = alertTypesConstants;
const { ARTICLE_LIST_IMAGE_WIDTH } = userProfileConstants;
const { PUBLISHED } = userArticlesTypes;
const { ARTICLE_CREATED_SUCCESS, ARTICLE_SUBMITTED_FAIL, ARTICLE_CREATED_FAIL } =
  myArticlesConstants;
export function* getUserArticlesSaga({ payload }: { payload: GetUserArticles }): Saga {
  try {
    const { articleType, page } = payload;
    const user = yield select(getUser);
    const userArticle = yield call([api.userArticle, 'getUserArticles'], {
      ...payload,
      id: user.id,
    });
    yield put(usersArticlesActions.getUserArticlesSuccess(userArticle, page, articleType));
  } catch (error) {
    yield put(usersArticlesActions.getUserArticlesFailed(error, payload.articleType));
    yield put(
      alertActions.setAlert({
        message: 'Oops! We cannot get your articles. Try again later.',
        type: DANGER,
      }),
    );
  }
}
export function* getUserArticleSaga({ payload }: { payload: string }): Saga {
  try {
    const user = yield select(getUser);
    const userArticle = yield call([api.userArticle, 'getUserArticle'], {
      userId: user.id,
      articleId: payload,
    });
    yield put(usersArticlesActions.getUserArticleSuccess(userArticle));
  } catch (error) {
    yield put(usersArticlesActions.getUserArticleFailed(error));
    yield put(
      alertActions.setAlert({
        message: 'Oops! We cannot get your article. Try again later.',
        type: DANGER,
      }),
    );
  }
}
export function* getOtherUserArticlesSaga({ payload }: { payload: number }): Saga {
  try {
    const user = yield select(getOtherUser);
    const { id: userId } = user;
    const callParams = {
      articleType: PUBLISHED,
      id: userId,
      page: payload,
      imageWidth: ARTICLE_LIST_IMAGE_WIDTH,
      isUGC: false,
    };
    const userArticle = yield call([api.userArticle, 'getUserArticles'], callParams);
    yield put(usersArticlesActions.getOtherUserArticlesSuccess(userArticle, payload));
  } catch (error) {
    const message = 'Oops! We cannot get this user articles. Try again later.';
    yield put(
      alertActions.setAlert({
        message,
        type: DANGER,
      }),
    );
    yield put(usersArticlesActions.getOtherUserArticlesFailed(error));
  }
}
export function* createArticlesSaga({ payload }: { payload: CreateArticlePayload }): Saga {
  try {
    const user = yield select(getUser);
    const articleCreated = yield call([api.userArticle, 'createArticle'], {
      ...payload,
      userId: user.id,
    });

    if (payload && payload.submittedForPublication) {
      yield put(push('/myarticles/articlesubmitted'));
    } else {
      yield put(
        alertActions.setAlert({
          message: ARTICLE_CREATED_SUCCESS,
          type: SUCCESS,
        }),
      );
    }

    yield put(usersArticlesActions.createArticleSuccess(articleCreated));
  } catch (error) {
    const message =
      payload && payload.submittedForPublication ? ARTICLE_SUBMITTED_FAIL : ARTICLE_CREATED_FAIL;
    yield put(
      alertActions.setAlert({
        message,
        type: DANGER,
      }),
    );
    yield put(usersArticlesActions.createArticleFailed(message));
  }
}
export function* updateArticlesSaga({ payload }: { payload: UpdateArticlePayload }): Saga {
  try {
    const user = yield select(getUser);
    const articleUpdated = yield call([api.userArticle, 'updateArticle'], {
      ...payload,
      userId: user.id,
    });

    if (payload && payload.submittedForPublication) {
      yield put(push('/myarticles/articlesubmitted'));
      yield put(usersArticlesActions.updateArticleSuccess(null));
    } else {
      yield put(
        alertActions.setAlert({
          message: myArticlesConstants.ARTICLE_UPDATED_SUCCESS,
          type: SUCCESS,
        }),
      );
      yield put(usersArticlesActions.updateArticleSuccess(articleUpdated));
    }
  } catch (error) {
    const message =
      payload && payload.submittedForPublication
        ? myArticlesConstants.ARTICLE_SUBMITTED_FAIL
        : myArticlesConstants.ARTICLE_UPDATED_FAIL;
    yield put(usersArticlesActions.updateArticleFailed(message));
    yield put(
      alertActions.setAlert({
        message,
        type: DANGER,
      }),
    );
  }
}
export function* autoSaveArticlesSaga({ payload }: { payload: UpdateArticlePayload }): Saga {
  try {
    const user = yield select(getUser);
    const createOrUpdate = payload.articleId ? 'updateArticle' : 'createArticle';
    const callPayload = { ...payload, userId: user.id };
    const articleUpdated = yield call([api.userArticle, createOrUpdate], callPayload);
    yield put(usersArticlesActions.performAutoSaveSuccess(articleUpdated));
  } catch (error) {
    const message = 'Auto Save failed';
    yield put(usersArticlesActions.performAutoSaveFailed(message));
  }
}
export default function* userArticleSaga(): Iterable<any> {
  yield takeEvery(articlesTypes.REQUEST, getUserArticlesSaga);
  yield takeEvery(articlesTypes.REQUEST_BY_ID, getUserArticleSaga);
  yield takeLatest(otherUserArticlesTypes.REQUEST, getOtherUserArticlesSaga);
  yield takeEvery(createArticleTypes.REQUEST, createArticlesSaga);
  yield takeEvery(updateArticleTypes.REQUEST, updateArticlesSaga);
  yield takeLatest(autoSaveArticleTypes.REQUEST, autoSaveArticlesSaga);
}
